import Dexie, { type Table } from 'dexie'
import {
  DB_NAME,
  DB_VERSION,
  META_ACTIVE_VERSION,
  SCHEMA,
  SCHEMA_V3,
  SCHEMA_V4,
  SCHEMA_V5,
  SCHEMA_V6,
  SCHEMA_V7,
  SCHEMA_V8,
  SCHEMA_V9,
  SCHEMA_V10,
  SCHEMA_V11,
  SCHEMA_V12,
  SCHEMA_V17
} from './schema'
import type {
  AssetRecord,
  BackupRecord,
  Cashier,
  CatalogVersion,
  Category,
  MetaEntry,
  OfflineQueueItem,
  OpenedContainer,
  PosShift,
  Product,
  QueuedSale,
  SaleReceipt,
  StockMovement,
  Writeoff
} from './models'

/** Схема v1 — нужна для корректной миграции на v2 (upgrade). */
const SCHEMA_V1: Record<string, string> = {
  products: '++id, &barcode, gtin, name, updatedAt',
  receipts: '++id, &receiptId, status, createdAt',
  offlineQueue: '++id, type, status, createdAt',
  backups: '++id, createdAt'
}

/** Имя стартовой версии каталога, создаваемой при миграции/первом старте. */
export const INITIAL_CATALOG_VERSION_NAME = 'Начальная версия'

/**
 * Локальная БД POS-системы поверх IndexedDB (Dexie).
 *
 * Хранит каталог, чеки, оффлайн-очередь и web-бэкапы.
 * Экземпляр создаётся один раз в bootstrap и прокидывается в сервисы.
 */
export class PosDatabase extends Dexie {
  // '!' — поля инициализируются Dexie через version().stores().
  products!: Table<Product, number>
  catalogVersions!: Table<CatalogVersion, number>
  /** Дерево категорий (tenant-scoped): верхний уровень и листья. */
  categories!: Table<Category, number>
  /** Бинарные ассеты: загруженные картинки категорий и товаров. */
  assets!: Table<AssetRecord, number>
  meta!: Table<MetaEntry, string>
  receipts!: Table<SaleReceipt, number>
  offlineQueue!: Table<OfflineQueueItem, number>
  backups!: Table<BackupRecord, number>
  cashiers!: Table<Cashier, string>
  sales_queue!: Table<QueuedSale, string>
  shifts!: Table<PosShift, number>
  opened_containers!: Table<OpenedContainer, number>
  /** Складской ledger: источник истины по остаткам (ARCH-TASK-65.10). */
  stock_movements!: Table<StockMovement, number>
  /** Документы списания (ARCH-TASK-65.10). */
  writeoffs!: Table<Writeoff, number>

  /**
   * @param name имя IndexedDB. По умолчанию — боевая `pos-system`;
   * интеграционные тесты передают уникальное имя для изоляции.
   */
  constructor(name: string = DB_NAME) {
    super(name)

    // v1 — исходная схема.
    this.version(1).stores(SCHEMA_V1)

    // v2 — версионирование каталога. Миграция: заводим стартовую версию и
    // привязываем к ней все существующие товары (data migration в upgrade).
    this.version(2)
      .stores(SCHEMA)
      .upgrade(async (tx) => {
        const now = Date.now()
        const versionId = await tx.table('catalogVersions').add({
          versionName: INITIAL_CATALOG_VERSION_NAME,
          note: 'Автомиграция v1 → v2',
          createdAt: now
        })
        await tx
          .table('products')
          .toCollection()
          .modify((p: Product) => {
            p.catalogVersionId = versionId as number
          })
        await tx.table('meta').put({
          key: META_ACTIVE_VERSION,
          value: versionId
        })
      })

    // v3 — облачная синхронизация: добавлены stores cashiers и sales_queue.
    // Чистое добавление stores, data-миграция не требуется.
    this.version(3).stores(SCHEMA_V3)

    // v4 — фикс iOS WebKit: убран избыточный `&` на первичных ключах
    // (meta/cashiers/sales_queue). Гарантирует накат чистой схемы на
    // устройствах, где база уже упала с ConstraintError.
    this.version(4).stores(SCHEMA_V4)

    // v5 — кассовые смены: добавлен store shifts (open/close, Z-отчёт).
    this.version(5).stores(SCHEMA_V5)

    // v6 — категории и весовые товары (FusionPOS): новые индексы products.
    // Чистое добавление индексов, data-миграция не требуется.
    this.version(6).stores(SCHEMA_V6)

    // v7 — вскрытие тары: новый store opened_containers (пул массы).
    this.version(7).stores(SCHEMA_V7)

    // v8 — заявки с касс: индексы clientUuid и cloudId (ARCH-TASK-58.1).
    this.version(8).stores(SCHEMA_V8)

    // v9 — возвраты: индекс originalReceiptId (ARCH-TASK-65.5).
    this.version(9).stores(SCHEMA_V9)

    // v10 — складской ledger и списания (ARCH-TASK-65.10).
    this.version(10).stores(SCHEMA_V10)

    // v11 — syncState для очередей ledger/writeoffs (65.10).
    this.version(11)
      .stores(SCHEMA_V11)
      .upgrade(async (tx) => {
        const backfill = async (table: string) => {
          await tx
            .table(table)
            .toCollection()
            .modify((row: { synced?: boolean; syncState?: 0 | 1 | 2 }) => {
              row.syncState = row.synced ? 1 : 0
            })
        }
        await backfill('writeoffs')
        await backfill('stock_movements')
      })

    // v12 — замена non_fiscal → fiscal (65.14): новые индексы receipts.
    // Чистое добавление индексов, data-миграция не требуется.
    this.version(12).stores(SCHEMA_V12)

    // v13 — смена в чеке для сверки документов ККТ.
    // v14 — подкатегории каталога.
    // v15 — дерево категорий и денормализованный верхний уровень у товара.
    // v16 — порядковый номер товара в категории и флаг активности.
    // v17 — бинарные ассеты (загруженные картинки).
    this.version(DB_VERSION).stores(SCHEMA_V17)
  }
}

/** Единый экземпляр БД для всего приложения. */
export const db = new PosDatabase()
